class ProductRecommendations extends HTMLElement {
  // Session page-views counter key
  static PV_KEY = 'exitIntentPageViews';

  // LocalStorage versioning
  static STORAGE_VERSION = 2;
  static VERSION_KEY = 'exitIntentStorageVersion';
  static RV_KEY = 'recentlyViewedProducts';
  static CURRENT_KEY = 'currentViewedProduct';

  constructor() {
    super();
    this.hasShown = false;
    this.useCookie = this.dataset.useCookie;
    this.expiringDays = this.dataset.cookieExpiration || 1;
    this.productsCount = Number(this.dataset.productsCount) || 6;
    this.sectionId = this.dataset.sectionId;
    this.showOncePerSession = this.dataset.showOnceSession === 'true';

    // Read page-views threshold from data attribute (fallback to 3 if missing/invalid)
    const pageViewsThreshold = Number(this.dataset.pageViewsThreshold);
    this.pageViewsThreshold = Number.isFinite(pageViewsThreshold) && pageViewsThreshold > 0 ? pageViewsThreshold : 0;

    this.storageVersion = ProductRecommendations.STORAGE_VERSION;
  }

  connectedCallback() {
    this._ensureStorageVersion();

    // Bind handler refs once
    this._onMouseOut = this._handleMouseOut.bind(this);
    this._onScroll = this._handleScroll.bind(this);

    // Increment session page views (once per page, even with multiple instances)
    this._incrementPageViewsOncePerPage();

    // Always prepare DOM references / initialize content first
    this.loadRecommendations();

    // Only arm exit-intent listeners when threshold is reached (>=)
    if (this._isEligibleByPageViews()) {
      this._armOnIdle();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('mouseout', this._onMouseOut);
    window.removeEventListener('scroll', this._onScroll);
    if (this._scrollTimeout) clearTimeout(this._scrollTimeout);
  }

  // ---------- Session page view gating ----------
  _readInt(v) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  }

  _getPageViews() {
    return this._readInt(sessionStorage.getItem(ProductRecommendations.PV_KEY));
  }

  /**
   * Count this page once per load:
   * - Uses a window-scoped flag to dedupe per page (multiple instances)
   * - Stores the running count in sessionStorage (cleared when tab closes)
   */
  _incrementPageViewsOncePerPage() {
    if (!window.__exitIntentPVIncremented) {
      const next = this._getPageViews() + 1;
      sessionStorage.setItem(ProductRecommendations.PV_KEY, String(next));
      window.__exitIntentPVIncremented = true;
    }
  }

  _isEligibleByPageViews() {
    // “Eligible on the 3rd page view and beyond”
    return this._getPageViews() >= this.pageViewsThreshold;
  }
  // ---------------------------------------------

  _ensureStorageVersion() {
    const readInt = (v) => {
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    };

    const localVer = readInt(localStorage.getItem(ProductRecommendations.VERSION_KEY));
    if (localVer < this.storageVersion) {
      localStorage.removeItem(ProductRecommendations.RV_KEY);
      localStorage.removeItem(ProductRecommendations.CURRENT_KEY);
      localStorage.setItem(ProductRecommendations.VERSION_KEY, String(this.storageVersion));
    } else if (localVer > this.storageVersion) {
      localStorage.setItem(ProductRecommendations.VERSION_KEY, String(this.storageVersion));
    }
  }

  _armOnIdle() {
    const arm = () => {
      document.addEventListener('mouseout', this._onMouseOut);
      window.addEventListener('scroll', this._onScroll);
    };

    const afterLoad = () => {
      // Wait for layout/scroll to settle before attaching listeners
      requestAnimationFrame(() => requestAnimationFrame(arm));
    };

    if (document.readyState === 'complete') {
      afterLoad();
    } else {
      window.addEventListener('load', afterLoad, { once: true });
    }
  }

  loadRecommendations() {
    const sectionId = this.dataset.sectionId;
    const carousel = this.querySelector(`.js-exit-intent--${sectionId} .js-exit-intent-carousel`);
    const swiperEl = this.querySelector(`.js-exit-intent--${sectionId} .swiper`);
    const nextEl = this.querySelector(`.js-exit-intent--${sectionId} .swiper-button-next`);
    const prevEl = this.querySelector(`.js-exit-intent--${sectionId} .swiper-button-prev`);
    const popupEl = this.querySelector(`.js-exit-intent--${sectionId} .js-exit-intent-modal`);
    const closeEls = this.querySelectorAll(`.js-exit-intent--${sectionId} .js-exit-intent-close`);
    const overlayEl = this.querySelector(`.js-exit-intent--${sectionId} .js-exit-intent-overlay`);

    this.refactorCarousel(carousel);
    const productSlides = carousel?.querySelectorAll('.swiper-slide') || [];
    if (productSlides.length === 0) {
      // No slides: still cache popup/overlay so triggers don't fail
      this._popupEl = popupEl;
      this._overlayEl = overlayEl;
      return;
    }

    this.initSwiper(swiperEl, nextEl, prevEl, carousel);
    this.popupInit(popupEl, overlayEl, closeEls);
    this.setupExitIntent(popupEl, overlayEl);
  }

  refactorCarousel(carousel) {
    if (!carousel) return;

    const STORAGE_KEY = 'recentlyViewedProducts';
    const storedData = JSON.parse(localStorage.getItem(STORAGE_KEY));
    const products = storedData?.products || [];
    const currentViewedProductId = JSON.parse(localStorage.getItem('currentViewedProduct'));

    if (products.length > 0) {
      const carouselContent = carousel.innerHTML;
      carousel.innerHTML = '';
      const existingIds = new Set();

      for (const product of products) {
        if (!product || !product.id) continue;

        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.setAttribute('data-product-id', product.id);
        slide.innerHTML = `
          <nova-product-card
            id="${product.id}"
            handle="${product.handle}"
            url="${product.url}"
            available="${product.available}"
            title="${product.title}"
            image="${product.featured_image}"
            image-alt="${product.title}"
            product-title="${product.title}"
            ${product.compare_at_price && product.compare_at_price !== product.price
              ? `compare-at-price="${product.compare_at_price}"`
              : ''
            }
            price="${product.price}"
            vendor="${product.vendor}"
            data-product-id="${product.id}"
          >
            <script type="application/json">
              {
                "variants": ${JSON.stringify(product.variants) || '[]'},
                "swatches": ${JSON.stringify(product.swatches || [])},
                "sizes": ${JSON.stringify(product.sizes || [])},
                "swatchesData": ${JSON.stringify(product.swatchesData || [])}
              }
            </script>
          </nova-product-card>
        `;
        carousel.appendChild(slide);
        existingIds.add(String(product.id));
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(carouselContent, 'text/html');
      const fallbackSlides = doc.body.children;

      for (const slide of fallbackSlides) {
        const id = slide.getAttribute('data-product-id');
        if (id && !existingIds.has(String(id))) {
          carousel.appendChild(slide.cloneNode(true));
        }
      }
    }

    const slides = Array.from(carousel.querySelectorAll('.swiper-slide'));
    let renderedCount = 0;

    for (const slide of slides) {
      const id = slide.dataset.productId;
      if (currentViewedProductId && String(id) === String(currentViewedProductId)) {
        slide.remove();
        continue;
      }
      if (renderedCount >= this.productsCount) {
        slide.remove();
        continue;
      }
      renderedCount++;
    }
  }

  initSwiper(swiperEl, nextEl, prevEl, carousel) {
    if (!swiperEl || typeof Swiper === 'undefined') return;

    const swiperAmount = swiperEl.querySelectorAll('.swiper-slide').length;
    if (swiperAmount <= 0) {
      nextEl?.classList.add('visually-hidden');
      prevEl?.classList.add('visually-hidden');
      return;
    } else if (swiperAmount === 1) {
      carousel.classList.add('exit-intent__center-carousel-sm');
    } else if (swiperAmount === 2) {
      carousel.classList.add('exit-intent__center-carousel-lg');
    }

    const swiper = new Swiper(swiperEl, {
      slidesPerView: 1.5,
      spaceBetween: 10,
      loop: false,
      navigation: { nextEl, prevEl },
      breakpoints: {
        500: { slidesPerView: 2, spaceBetween: 10 },
        1024: { slidesPerView: 3, spaceBetween: 20 },
      },
      on: {
        afterInit: function () {
          toggleNav(this);
        },
        resize: function () {
          toggleNav(this);
        }
      }
    });

    function toggleNav(swiper) {
      const visibleSlides = getSlidesPerView(swiper);
      const totalSlides = swiper.slides.length;

      if (totalSlides <= visibleSlides) {
        nextEl?.classList.add('visually-hidden');
        prevEl?.classList.add('visually-hidden');
      } else {
        nextEl?.classList.remove('visually-hidden');
        prevEl?.classList.remove('visually-hidden');
      }
    }

    function getSlidesPerView(swiper) {
      const breakpoints = swiper.params.breakpoints;
      const width = window.innerWidth;
      let slidesPerView = swiper.params.slidesPerView;

      if (breakpoints) {
        const matched = Object.keys(breakpoints)
          .map(n => Number(n))
          .sort((a, b) => b - a)
          .find(bp => width >= bp);

        if (matched) {
          slidesPerView = breakpoints[matched].slidesPerView;
        }
      }

      return typeof slidesPerView === 'number' ? slidesPerView : 1;
    }
  }

  popupInit(popupEl, overlayEl, closeEls) {
    if (!popupEl || closeEls.length === 0) return;
    closeEls.forEach((closeEl) => {
      closeEl.addEventListener('click', () => {
        popupEl.classList.remove('is-active');
        overlayEl.classList.remove('is-active');
      });
    });
  }

  showPopup(popupEl, overlayEl) {
    if (!popupEl || !overlayEl) return;
    popupEl.classList.add('is-active');
    overlayEl.classList.add('is-active');
  }

  setupExitIntent(popupEl, overlayEl) {
    // Do not gate threshold here; that's handled when arming listeners
    if (
      this.getCookie('exitpopup') === 'hide' ||
      this.hasShown ||
      (this.showOncePerSession && sessionStorage.getItem('exitIntentShown') === 'true')
    ) {
      return;
    }

    this._lastScroll = window.pageYOffset;
    this._maxScroll = this._lastScroll;

    this._popupEl = popupEl;
    this._overlayEl = overlayEl;
  }

  _handleMouseOut(e) {
    // Standard top-leave heuristic for exit intent
    const isLeaving = !e.toElement && !e.relatedTarget;
    if (isLeaving && !this.hasShown) {
      if (!this._popupEl || !this._overlayEl) return;
      this.triggerExitIntent(this._popupEl, this._overlayEl);
    }
  }

  _handleScroll() {
    const currentScroll = window.pageYOffset;
    const delta = currentScroll - this._lastScroll;
    const deltaThreshold = window.innerWidth < 768 ? -30 : -50;

    if (delta < deltaThreshold && !this.hasShown) {
      if (!this._popupEl || !this._overlayEl) return;
      this.triggerExitIntent(this._popupEl, this._overlayEl);
    }

    if (currentScroll > this._maxScroll) {
      this._maxScroll = currentScroll;
    }

    if (
      this._maxScroll > window.innerHeight * 2 &&
      currentScroll < this._maxScroll - window.innerHeight * 0.1 &&
      !this.hasShown
    ) {
      if (!this._popupEl || !this._overlayEl) return;
      this.triggerExitIntent(this._popupEl, this._overlayEl);
    }

    this._lastScroll = currentScroll;
    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => {
      this._lastScroll = currentScroll;
    }, 100);
  }

  triggerExitIntent(popupEl, overlayEl) {
    this.showPopup(popupEl, overlayEl);
    this.hasShown = true;

    if (this.useCookie === 'true') {
      this.setCookie('exitpopup', 'hide', this.expiringDays);
    }

    if (this.showOncePerSession) {
      sessionStorage.setItem('exitIntentShown', 'true');
    }
  }

  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      expires = '; expires=' + d.toGMTString();
    }
    document.cookie = `${name}=${value}${expires}; path=/`;
  }

  getCookie(name) {
    const prefix = name + '=';
    return document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(prefix))?.slice(prefix.length) || null;
  }
}

customElements.define('exit-intent-recommendations', ProductRecommendations);
